import { jsPDF } from "jspdf";
import DOMPurify from "dompurify";
import type { ExportSettings } from "@/lib/export-settings";
import { DEFAULT_EXPORT_SETTINGS } from "@/lib/export-settings";
import type {
  TaskDetail,
  ChecklistItem,
  TaskAnimalLink,
  TaskInteractionLink,
  TaskLabelAssignment,
} from "@/components/tasks/task-types";
import { formatUserName } from "@/lib/task-query-utils";
import { getBoardTypeConfig } from "@/lib/board-types";

export interface MeetingExportData {
  task: TaskDetail;
  notes: string | null;
  isMeeting: boolean;
  boardType?: string;
}

function resolveSettings(raw: ExportSettings | null | undefined): ExportSettings {
  return raw ?? DEFAULT_EXPORT_SETTINGS;
}

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return "";
  const d = new Date(dateStr + "T00:00:00");
  return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" });
}

function safeSetHtml(el: HTMLElement, html: string): void {
  el.innerHTML = DOMPurify.sanitize(html);
}

export function stripHtml(html: string): string {
  const div = document.createElement("div");
  safeSetHtml(div, html);
  return (div.textContent || div.innerText || "").trim();
}

export function htmlToMarkdown(html: string): string {
  const div = document.createElement("div");
  safeSetHtml(div, html);
  const lines: string[] = [];

  function processNode(node: Node): string {
    if (node.nodeType === Node.TEXT_NODE) {
      return node.textContent || "";
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return "";
    const el = node as HTMLElement;
    const tag = el.tagName.toLowerCase();
    const childContent = Array.from(el.childNodes).map(processNode).join("");

    switch (tag) {
      case "h1": return `\n# ${childContent.trim()}\n`;
      case "h2": return `\n## ${childContent.trim()}\n`;
      case "h3": return `\n### ${childContent.trim()}\n`;
      case "h4": return `\n#### ${childContent.trim()}\n`;
      case "p": return `\n${childContent.trim()}\n`;
      case "br": return "\n";
      case "strong":
      case "b": return `**${childContent}**`;
      case "em":
      case "i": return `*${childContent}*`;
      case "ul": {
        const items: string[] = [];
        el.querySelectorAll(":scope > li").forEach(li => {
          items.push(`- ${(li.textContent || "").trim()}`);
        });
        return "\n" + items.join("\n") + "\n";
      }
      case "ol": {
        const items: string[] = [];
        let idx = 1;
        el.querySelectorAll(":scope > li").forEach(li => {
          items.push(`${idx}. ${(li.textContent || "").trim()}`);
          idx++;
        });
        return "\n" + items.join("\n") + "\n";
      }
      case "li": return childContent;
      case "blockquote": return `\n> ${childContent.trim()}\n`;
      case "code": return `\`${childContent}\``;
      case "pre": return `\n\`\`\`\n${childContent.trim()}\n\`\`\`\n`;
      default: return childContent;
    }
  }

  const result = Array.from(div.childNodes).map(processNode).join("");
  return result.replace(/\n{3,}/g, "\n\n").trim();
}

export function htmlToFormattedLines(html: string): string[] {
  const div = document.createElement("div");
  safeSetHtml(div, html);
  const lines: string[] = [];

  function walkNodes(node: Node) {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent || "";
      if (text.trim()) lines.push(text.trim());
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    const el = node as HTMLElement;
    const tag = el.tagName.toLowerCase();

    switch (tag) {
      case "h1":
      case "h2":
      case "h3":
      case "h4":
        lines.push("");
        lines.push((el.textContent || "").trim().toUpperCase());
        lines.push("");
        break;
      case "p":
        lines.push((el.textContent || "").trim());
        lines.push("");
        break;
      case "br":
        lines.push("");
        break;
      case "ul":
        el.querySelectorAll(":scope > li").forEach(li => {
          lines.push(`  \u2022  ${(li.textContent || "").trim()}`);
        });
        lines.push("");
        break;
      case "ol": {
        let idx = 1;
        el.querySelectorAll(":scope > li").forEach(li => {
          lines.push(`  ${idx}. ${(li.textContent || "").trim()}`);
          idx++;
        });
        lines.push("");
        break;
      }
      case "blockquote":
        lines.push(`  > ${(el.textContent || "").trim()}`);
        lines.push("");
        break;
      default:
        Array.from(el.childNodes).forEach(walkNodes);
        break;
    }
  }

  Array.from(div.childNodes).forEach(walkNodes);
  while (lines.length > 0 && lines[lines.length - 1] === "") lines.pop();
  while (lines.length > 0 && lines[0] === "") lines.shift();
  return lines;
}

export function sanitizeFilename(name: string): string {
  return name.replace(/[<>:"/\\|?*]/g, "_").replace(/\s+/g, " ").trim();
}

function formatAnimalLine(a: TaskAnimalLink): string {
  const parts = [a.animalName ?? a.animalId];
  if (a.animalName && a.animalId) parts[0] += ` (${a.animalId})`;
  if (a.animalSpecies) parts.push(a.animalSpecies);
  if (a.locationCode) parts.push(a.locationCode);
  return parts.join(" \u00B7 ");
}

function formatInteractionLine(i: TaskInteractionLink): string {
  const parts = [i.animalName ?? i.animalId ?? "Interaction"];
  if (i.animalName && i.animalId) parts[0] += ` (${i.animalId})`;
  if (i.eventType) parts.push(i.eventType);
  if (i.occurredAt) parts.push(new Date(i.occurredAt).toLocaleDateString());
  return parts.join(" \u00B7 ");
}

function resolveExportBoardType(data: MeetingExportData): string {
  return data.boardType || (data.isMeeting ? "meeting" : "regular");
}

export function getMeetingIncompleteFields(
  data: MeetingExportData,
  exportSettings?: ExportSettings | null,
): string[] {
  const settings = resolveSettings(exportSettings);
  const config = getBoardTypeConfig(resolveExportBoardType(data));
  const missing: string[] = [];

  if (settings.sections.date && !data.task.dueDate) {
    missing.push(config.labels.exportDateLabel);
  }
  if (settings.sections.description && !data.task.description?.trim()) {
    missing.push(config.labels.description);
  }
  if (settings.sections.agendaItems && data.task.checklistItems.length === 0) {
    missing.push(config.labels.checklistItems);
  }
  if (settings.sections.animals && data.task.animals.length === 0) {
    missing.push("Animals");
  }
  if (settings.sections.interactions && data.task.interactions.length === 0) {
    missing.push("Interactions");
  }
  if (settings.sections.notes) {
    const notesText = data.notes ? stripHtml(data.notes) : "";
    if (!notesText) {
      missing.push("Notes");
    }
  }

  return missing;
}

export function formatMeetingMarkdown(
  data: MeetingExportData,
  exportSettings?: ExportSettings | null,
): string {
  const settings = resolveSettings(exportSettings);
  const { task, notes } = data;
  const config = getBoardTypeConfig(resolveExportBoardType(data));
  const isMeeting = resolveExportBoardType(data) === "meeting";
  const lines: string[] = [];

  lines.push(`# ${task.title}`);

  if (settings.sections.date && task.dueDate) {
    lines.push(`**${config.labels.exportDateLabel}:** ${formatDate(task.dueDate)}`);
  }

  if (settings.sections.metadata) {
    const assigneeName = formatUserName(task.assignee);
    if (isMeeting && assigneeName) {
      lines.push(`**${config.labels.exportAssigneeLabel}:** ${assigneeName}`);
    }
    const metaParts: string[] = [];
    if (task.status?.name) metaParts.push(`**Status:** ${task.status.name}`);
    if (!isMeeting && assigneeName) {
      metaParts.push(`**${config.labels.exportAssigneeLabel}:** ${assigneeName}`);
    }
    if (task.priority) metaParts.push(`**Priority:** ${task.priority}`);
    if (task.labels.length > 0) {
      metaParts.push(`**Labels:** ${task.labels.map((l) => l.labelName).join(", ")}`);
    }
    if (metaParts.length > 0) {
      lines.push(metaParts.join(" | "));
    }
  }

  if (settings.sections.date || settings.sections.metadata) {
    lines.push("");
  }

  if (settings.sections.description && task.description?.trim()) {
    lines.push(`## ${config.labels.exportDescriptionHeading}`);
    lines.push(task.description.trim());
    lines.push("");
  }

  if (settings.sections.agendaItems && task.checklistItems.length > 0) {
    lines.push(`## ${config.labels.checklistItems}`);
    const sorted = [...task.checklistItems].sort((a, b) => a.sortOrder - b.sortOrder);
    for (const item of sorted) {
      const check = item.isDone ? "x" : " ";
      lines.push(`- [${check}] ${item.text}`);
    }
    lines.push("");
  }

  if (settings.sections.animals && task.animals.length > 0) {
    lines.push("## Animals");
    for (const a of task.animals) {
      lines.push(`- ${formatAnimalLine(a)}`);
    }
    lines.push("");
  }

  if (settings.sections.interactions && task.interactions.length > 0) {
    lines.push("## Interactions");
    for (const i of task.interactions) {
      lines.push(`- ${formatInteractionLine(i)}`);
    }
    lines.push("");
  }

  if (settings.sections.notes) {
    const notesText = notes ? stripHtml(notes) : "";
    if (notesText) {
      lines.push(`## ${config.labels.exportNotesHeading}`);
      lines.push(htmlToMarkdown(notes!));
      lines.push("");
    }
  }

  return lines.join("\n").trimEnd() + "\n";
}

export function generateMeetingPdf(
  data: MeetingExportData,
  exportSettings?: ExportSettings | null,
): void {
  const settings = resolveSettings(exportSettings);
  const { task, notes } = data;
  const config = getBoardTypeConfig(resolveExportBoardType(data));
  const isMeeting = resolveExportBoardType(data) === "meeting";

  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 20;
  const contentWidth = pageWidth - margin * 2;
  let y = margin;

  const lineHeight = 6;
  const sectionGap = 4;

  function checkPageBreak(needed: number) {
    const pageHeight = doc.internal.pageSize.getHeight();
    if (y + needed > pageHeight - margin) {
      doc.addPage();
      y = margin;
    }
  }

  function addWrappedText(text: string, fontSize: number, opts?: { bold?: boolean; color?: [number, number, number] }) {
    doc.setFontSize(fontSize);
    doc.setFont("helvetica", opts?.bold ? "bold" : "normal");
    if (opts?.color) {
      doc.setTextColor(...opts.color);
    } else {
      doc.setTextColor(30, 30, 30);
    }
    const splitLines = doc.splitTextToSize(text, contentWidth);
    for (const line of splitLines) {
      checkPageBreak(lineHeight);
      doc.text(line, margin, y);
      y += lineHeight;
    }
  }

  doc.setFontSize(18);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(20, 20, 20);
  const titleLines = doc.splitTextToSize(task.title, contentWidth);
  for (const line of titleLines) {
    checkPageBreak(8);
    doc.text(line, margin, y);
    y += 8;
  }
  y += 2;

  if (settings.sections.date && task.dueDate) {
    addWrappedText(formatDate(task.dueDate), 10, { color: [100, 100, 100] });
    y += 1;
  }

  if (settings.sections.metadata) {
    const assigneeName = formatUserName(task.assignee);
    if (isMeeting && assigneeName) {
      addWrappedText(`${config.labels.exportAssigneeLabel}: ${assigneeName}`, 10, { color: [80, 80, 80] });
    }
    const metaParts: string[] = [];
    if (task.status?.name) metaParts.push(`Status: ${task.status.name}`);
    if (!isMeeting && assigneeName) {
      metaParts.push(`${config.labels.exportAssigneeLabel}: ${assigneeName}`);
    }
    if (task.priority) metaParts.push(`Priority: ${task.priority}`);
    if (task.labels.length > 0) {
      metaParts.push(`Labels: ${task.labels.map((l) => l.labelName).join(", ")}`);
    }
    if (metaParts.length > 0) {
      addWrappedText(metaParts.join("  |  "), 9, { color: [80, 80, 80] });
    }
    y += 1;
  }

  y += sectionGap;

  function addSectionHeading(text: string) {
    checkPageBreak(lineHeight + sectionGap);
    doc.setFontSize(13);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(30, 30, 30);
    doc.text(text, margin, y);
    y += 7;
    doc.setDrawColor(200, 200, 200);
    doc.line(margin, y - 2, margin + contentWidth, y - 2);
    y += 2;
  }

  if (settings.sections.description && task.description?.trim()) {
    addSectionHeading(config.labels.exportDescriptionHeading);
    addWrappedText(task.description.trim(), 10);
    y += sectionGap;
  }

  if (settings.sections.agendaItems && task.checklistItems.length > 0) {
    addSectionHeading(config.labels.checklistItems);
    const sorted = [...task.checklistItems].sort((a, b) => a.sortOrder - b.sortOrder);
    for (const item of sorted) {
      const prefix = item.isDone ? "[x]" : "[  ]";
      checkPageBreak(lineHeight);
      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(30, 30, 30);

      doc.text(prefix, margin + 2, y);
      const textLines = doc.splitTextToSize(item.text, contentWidth - 14);
      for (let j = 0; j < textLines.length; j++) {
        if (j > 0) checkPageBreak(lineHeight);
        doc.text(textLines[j], margin + 14, y);
        y += lineHeight;
      }
    }
    y += sectionGap;
  }

  if (settings.sections.animals && task.animals.length > 0) {
    addSectionHeading("Animals");
    for (const a of task.animals) {
      const line = formatAnimalLine(a);
      checkPageBreak(lineHeight);
      addWrappedText(`\u2022  ${line}`, 10);
    }
    y += sectionGap;
  }

  if (settings.sections.interactions && task.interactions.length > 0) {
    addSectionHeading("Interactions");
    for (const i of task.interactions) {
      const line = formatInteractionLine(i);
      checkPageBreak(lineHeight);
      addWrappedText(`\u2022  ${line}`, 10);
    }
    y += sectionGap;
  }

  if (settings.sections.notes) {
    const notesText = notes ? stripHtml(notes) : "";
    if (notesText) {
      addSectionHeading(config.labels.exportNotesHeading);
      const formattedLines = htmlToFormattedLines(notes!);
      for (const line of formattedLines) {
        if (line === "") {
          y += 2;
        } else if (line === line.toUpperCase() && line.length > 0 && !/^\s/.test(line)) {
          y += 2;
          addWrappedText(line, 11, { bold: true });
          y += 1;
        } else if (line.startsWith("  \u2022  ") || /^\s+\d+\.\s/.test(line)) {
          addWrappedText(line, 10);
        } else if (line.startsWith("  > ")) {
          addWrappedText(line, 10, { color: [100, 100, 100] });
        } else {
          addWrappedText(line, 10);
        }
      }
      y += sectionGap;
    }
  }

  const dateStr = task.dueDate ? formatDate(task.dueDate) : "No Date";
  const filename = sanitizeFilename(`${task.title} - ${dateStr}`) + ".pdf";
  doc.save(filename);
}
